import { Checkbox, Form, Input } from 'antd'
import { CheckboxChangeEvent } from 'antd/lib/checkbox'
import React, { FC, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Link } from 'react-router-dom'
import { FeedbackAPI } from '../../shared/api/feedback'
import { H2, H4, Toast } from '../../shared/components'
import { EMAIL_REG_EXP, REQUIRED_FIELD } from '../../shared/constants/auth'
import { useAuth } from '../../shared/contexts/authContext'
import { FeedbackData } from '../../shared/models/auth'
import {
  FeedbackAreas,
  FeedbackInformation,
  FeedbackPageTitles,
  FeedbackSuccessViewStrings,
} from '../../shared/strings/FeedbackContent'
import { PrivacyStatementStrings } from '../../shared/strings/PrivacyStatementContent'
import {
  PageTitleRow,
  ErrorMessage,
  FeedbackCheckbox,
  FeedbackForm,
  FormElement,
  InfoLabel,
  InputDescription,
  Label,
  MessageFormElement,
  PageDescription,
  SendButton,
} from './Styles'

const FeedbackSuccessView: FC = () => {
  return (
    <>
      <H2 id="thank-you-for-your-feedback-message-js">{FeedbackSuccessViewStrings.title}</H2>
      <H4>{FeedbackSuccessViewStrings.message}</H4>
    </>
  )
}

const Feedback: FC = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [checkedItems, setCheckedItems] = useState<any[]>([])
  const { currentUser } = useAuth()
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    setValue,
  } = useForm()
  const [sendFeedbackError, setSendFeedbackError] = useState<string | undefined>()
  const [isSendFeedbackErrorVisible, setIsSendFeedbackErrorVisible] = useState(false)
  const [isShowSuccessView, setIsShowSuccessView] = useState(false)

  useEffect(() => {
    register('feedbackAreas', { validate: feedbackAreasValidation })
  }, [])

  const handleChange = (e: CheckboxChangeEvent) => {
    let updatedCheckedItems = []

    if (e.target.checked) {
      updatedCheckedItems = [...checkedItems, e.target.value]
    } else {
      updatedCheckedItems = checkedItems.filter((element) => element !== e.target.value)
    }

    setCheckedItems(updatedCheckedItems)
    setValue('feedbackAreas', updatedCheckedItems, { shouldValidate: true })
  }

  const feedbackAreasValidation = (feedbackAreas?: string[]) => {
    if (feedbackAreas && feedbackAreas.length > 0) {
      return true
    }
    return REQUIRED_FIELD
  }

  async function onSend(data: FeedbackData) {
    setIsSendFeedbackErrorVisible(false)
    setIsLoading(true)
    const feedbackAreas = checkedItems
    const message = data.message
    const name = data.name
    const email = data.email
    const company = data.company

    try {
      await FeedbackAPI.sendFeedback({ feedbackAreas, message, name, email, company })
      setIsLoading(false)
      setIsShowSuccessView(true)
    } catch (e) {
      setSendFeedbackError(`${FeedbackInformation.sendingFeedbackFailed} ${e?.response?.data?.message || e?.message}`)
      setIsSendFeedbackErrorVisible(true)
      setIsLoading(false)
    }
  }

  const feedbackAreas = [
    {
      number: 1,
      title: FeedbackAreas.area1,
    },
    {
      number: 2,
      title: FeedbackAreas.area2,
    },
    {
      number: 3,
      title: FeedbackAreas.area3,
    },
    {
      number: 4,
      title: FeedbackAreas.area4,
    },
    {
      number: 5,
      title: FeedbackAreas.area5,
    },
  ]

  if (isShowSuccessView) {
    return <FeedbackSuccessView />
  }

  return (
    <>
      <PageTitleRow>
        <H2>{FeedbackPageTitles.title}</H2>
        <Toast
          isVisible={isSendFeedbackErrorVisible}
          position="absolute"
          text={sendFeedbackError}
          onDismiss={() => setIsSendFeedbackErrorVisible(false)}
          type="error"
        />
      </PageTitleRow>
      <PageDescription>{FeedbackPageTitles.description}</PageDescription>
      <FeedbackForm>
        <form onSubmit={handleSubmit(onSend)}>
          <Form.Item>
            <Label>{FeedbackAreas.title}</Label>
            <Checkbox.Group>
              {feedbackAreas.map((feedbackArea) => (
                <React.Fragment key={feedbackArea.number}>
                  <FeedbackCheckbox name={feedbackArea.title} value={feedbackArea.title} onChange={handleChange}>
                    {feedbackArea.title}
                  </FeedbackCheckbox>
                  <br />
                </React.Fragment>
              ))}
            </Checkbox.Group>
            {errors.feedbackAreas && (
              <ErrorMessage id="feedback-areas-input-error-js">
                {errors.feedbackAreas.message || REQUIRED_FIELD}
              </ErrorMessage>
            )}
          </Form.Item>
          <Form.Item>
            <Label htmlFor="feedback-message-input-js">{FeedbackInformation.message}</Label>
            <MessageFormElement>
              <Controller
                render={({ field }) => <InputDescription id="feedback-message-input-js" {...field} rows={6} />}
                name="message"
                control={control}
                defaultValue=""
                rules={{
                  required: true,
                }}
              />
              {errors.message && <ErrorMessage id="message-input-error-js">{REQUIRED_FIELD}</ErrorMessage>}
            </MessageFormElement>
          </Form.Item>
          <Form.Item>
            <Label htmlFor="name-input-js">{FeedbackInformation.name}</Label>
            <FormElement>
              <Controller
                render={({ field }) => <Input id="name-input-js" {...field} />}
                name="name"
                control={control}
                defaultValue={`${currentUser?.firstName} ${currentUser?.lastName}`.trim()}
                rules={{
                  required: true,
                }}
              />
            </FormElement>
            {errors.name && <ErrorMessage id="name-input-error-js">{REQUIRED_FIELD}</ErrorMessage>}
          </Form.Item>
          <Form.Item>
            <Label htmlFor="email-input-js">{FeedbackInformation.email}</Label>
            <FormElement>
              <Controller
                render={({ field }) => <Input id="email-input-js" {...field} />}
                name="email"
                control={control}
                defaultValue={currentUser?.email}
                rules={{
                  pattern: {
                    value: EMAIL_REG_EXP,
                    message: FeedbackInformation.invalidEmailAddressError,
                  },
                  required: true,
                }}
              />
            </FormElement>
            {errors.email && (
              <ErrorMessage id="email-input-error-js">{errors.email.message || REQUIRED_FIELD}</ErrorMessage>
            )}
          </Form.Item>
          <Form.Item>
            <Label htmlFor="company-name-input-js">{FeedbackInformation.company}</Label>
            <FormElement>
              <Controller
                render={({ field }) => <Input id="company-name-input-js" {...field} />}
                name="company"
                control={control}
                defaultValue=""
                rules={{
                  required: false,
                }}
              />
            </FormElement>
          </Form.Item>
          <SendButton block id="send-feedback-button-js" type="primary" loading={isLoading} htmlType="submit">
            {FeedbackInformation.sendFormButton}
          </SendButton>
        </form>
        <InfoLabel>
          {PrivacyStatementStrings.pleaseSeePrivacyStatement}
          <Link to="/privacy-statement" target="_blank" rel="noreferrer">
            {PrivacyStatementStrings.privacyStatement}
          </Link>
        </InfoLabel>
      </FeedbackForm>
    </>
  )
}

export default Feedback
